/**
 * Sign in.
 *
 * Email + password and nothing else: `users.email` is globally unique, so the
 * address resolves the workspace and there is no picker to show.
 *
 * The form validates against `loginRequestSchema` — the same contract the API
 * validates against — so the round trip is skipped only for input the API would
 * have rejected anyway. It never applies the *password policy* here: that
 * belongs to setting a password, and applying it at sign-in would both lock out
 * an existing user after a policy change and hint at the stored value.
 */
import { loginRequestSchema } from '@wend/shared';
import { Button, FormField, Input } from '@wend/ui';
import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/auth-context';
import { AuthLayout } from '../components/AuthLayout';
import { FormAlert } from '../components/FormAlert';
import { apiFieldErrors, errorMessage, isApiError, ROOT_FIELD } from '../lib/api/errors';
import { validate, withoutError, type FormErrors } from '../lib/form';

interface LocationState {
  from?: unknown;
}

/** Only same-app paths are honoured, so a crafted history entry cannot bounce
 *  a freshly-authenticated user to another origin. */
function safeReturnPath(state: unknown): string {
  const from = (state as LocationState | null)?.from;
  if (typeof from !== 'string') return '/';
  if (!from.startsWith('/') || from.startsWith('//')) return '/';
  return from;
}

export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;

    setRequestId(undefined);

    const result = validate(loginRequestSchema, { email, password });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setPending(true);

    try {
      await signIn(result.data);
      navigate(safeReturnPath(location.state), { replace: true });
    } catch (error) {
      // A failed sign-in says one thing whatever the cause — wrong password,
      // unknown address, deactivated account. The API is deliberate about that;
      // this screen must not undo it by inferring more than it was told.
      setErrors({ ...apiFieldErrors(error), [ROOT_FIELD]: errorMessage(error) });
      setRequestId(isApiError(error) ? error.requestId : undefined);
      setPending(false);
    }
  }

  return (
    <AuthLayout
      title="Sign in to Wend"
      description="Use the email address your workspace was set up with."
      footer={
        <p>
          Waiting on an invitation? The welcome email carries a link that sets your password —{' '}
          <Link
            to="/set-password"
            className="text-brand-b underline underline-offset-4 hover:text-text"
          >
            request a new one
          </Link>{' '}
          if it has expired.
        </p>
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
        {errors[ROOT_FIELD] !== undefined && (
          <FormAlert tone="error" requestId={requestId}>
            {errors[ROOT_FIELD]}
          </FormAlert>
        )}

        <FormField label="Email" error={errors.email} required>
          <Input
            type="email"
            name="email"
            autoComplete="email"
            autoFocus
            required
            placeholder="you@company.com"
            value={email}
            disabled={pending}
            onChange={(event) => {
              setEmail(event.target.value);
              setErrors((current) => withoutError(current, 'email'));
            }}
          />
        </FormField>

        <FormField label="Password" error={errors.password} required>
          <Input
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            disabled={pending}
            onChange={(event) => {
              setPassword(event.target.value);
              setErrors((current) => withoutError(current, 'password'));
            }}
          />
        </FormField>

        <Button type="submit" loading={pending} fullWidth>
          {pending ? 'Signing in' : 'Sign in'}
        </Button>
      </form>
    </AuthLayout>
  );
}
